/**
 * Per-exchange API URL registry. The widget resolves its backend
 * GraphQL endpoint from `?exchange=<name>` in the iframe query string;
 * this map is the single source of truth.
 *
 * Why exchange-name → URL instead of `?apiUrl=`:
 *   - host apps don't have to know the backend host (just the exchange);
 *   - rotating a tunnel/host means one map edit, not every embed URL;
 *   - prevents `?apiUrl=` injection: only known exchanges resolve.
 *
 * Add new exchanges here (and ensure the BE for that exchange echoes
 * the same `EXCHANGE` constant so it round-trips cleanly).
 */
const EXCHANGE_API_URLS: Readonly<Record<string, string>> = {
  ANDX: "https://vakotrade-andx.cryptosrvc.com/graphql",
  "ANDX-UAT": "https://vakotrade-andx-uat.cryptosrvc.com/graphql",
  // VAKOTEST is the exchange name exchange-dev.shiftmarkets.com hands
  // to the iframe, so it has to resolve to whatever BE the host is
  // currently storing tokens in — the dev vakotrade backend.
  VAKOTEST: "https://vakotrade.cryptosrvc-dev.com/graphql",
  // The CXM tenant's UAT backend, and where CXM testing runs now. The
  // partner gateway is reached from there rather than from a developer
  // machine, which is what a stable egress IP for CXM to whitelist needs.
  "CXM-UAT": "https://vakotrade-cxm-uat.cryptosrvc.com/graphql",
  // Local-dev only. The CXM gateway is not reachable from our dev server
  // (its host does not resolve outside CXM's network), so end-to-end CXM
  // testing runs against a vakotrade started on the developer's own machine,
  // which is where the VPN is. Also selects the CXM forms, since the widget
  // picks the PSP from any `CXM-*` exchange name.
  "CXM-LOCAL": "http://localhost:3000/graphql",
};

/**
 * Allow-list of legacy iframe-supplied `?apiUrl=` values. Kept only so
 * older `payments_routes.fiat_iframe_*_url` entries that still pass
 * `?apiUrl=` continue to load while we migrate hosts to the
 * exchange-name flow above.
 */
const LEGACY_ALLOWED_APIURLS: readonly string[] = Object.values(EXCHANGE_API_URLS);

/**
 * Resolve the widget's backend URL from the iframe URL params.
 * Preference order:
 *   1. `?exchange=<name>` → exchange map (preferred; new flow).
 *   2. `?apiUrl=<url>`   → legacy whitelist (kept as fallback).
 *
 * Returns `null` when neither resolves — caller surfaces the
 * "configure apiUrl" warning state.
 */
export const getSafeApiUrl = (
  exchangeFromParams?: string | null,
  apiUrlFromParams?: string | null,
): string | null => {
  if (exchangeFromParams) {
    const url = EXCHANGE_API_URLS[exchangeFromParams.toUpperCase()];
    if (url) return url;
    console.warn(
      `Unknown exchange "${exchangeFromParams}" — not in EXCHANGE_API_URLS map.`,
    );
  }

  if (apiUrlFromParams && LEGACY_ALLOWED_APIURLS.includes(apiUrlFromParams)) {
    return apiUrlFromParams;
  }

  if (!exchangeFromParams && !apiUrlFromParams) {
    console.warn(
      "Neither ?exchange nor ?apiUrl supplied. Add ?exchange=<name> to the iframe URL.",
    );
  }
  return null;
};
